import { Box, Paper, Typography } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material/Select'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { CountryCode } from '@/components/dataDisplay/FlagIcon'
import FlagIcon from '@/components/dataDisplay/FlagIcon'
import CustomFormControl from '@/components/inputs/CustomFormControl'
import type { GroupedSelectFieldOptions } from '@/components/inputs/CustomSelectField'
import CustomSelectField from '@/components/inputs/CustomSelectField'
import PageHeader from '@/components/layouts/PageHeader'
import { FIELD_WIDTH } from '@/constants/layout'
import ActivationPlanChart from '@/features/activationPlan/components/ActivationPlanChart'
import { useActivationSchedulesByRegulationObjectQuery } from '@/features/activationPlan/hooks/useActivationSchedulesByRegulationObjectQuery'
import { usePortfoliosQuery } from '@/features/bidding/hooks/usePortfoliosQuery'

function ActivationPlanPage() {
  const { t, i18n } = useTranslation()
  const [portfolioCode, setPortfolioCode] = useState<string>('')
  const [deliveryDay, setDeliveryDay] = useState<DateTime>(DateTime.now().plus({ days: 1 }))

  const { portfolios } = usePortfoliosQuery({})

  const groupedPortfolioOptions = useMemo((): GroupedSelectFieldOptions<string>[] => {
    if (!portfolios) return []

    const grouped = portfolios.reduce<Record<string, typeof portfolios>>((acc, portfolio) => {
      const country = portfolio.countryCode
      if (!acc[country]) {
        acc[country] = []
      }
      acc[country].push(portfolio)
      return acc
    }, {})

    return Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b))
      .map((countryCode) => ({
        groupKey: countryCode,
        groupLabel: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlagIcon countryCode={countryCode.toLowerCase() as CountryCode} />
            {t(`common.country.${countryCode.toLowerCase() as CountryIdentifier}`)}
          </Box>
        ),
        options: grouped[countryCode]
          .toSorted((a, b) => a.code.localeCompare(b.code))
          .map((p) => ({
            value: p.code,
            id: `portfolio-selector-item-${p.code}`,
            label: p.code,
          })),
      }))
  }, [portfolios, t])

  const deliveryDayString = deliveryDay.toISODate()

  return (
    <>
      <PageHeader
        isRoot
        appBarContent={
          <Box alignItems="center" display="flex" flexDirection="row" gap={2}>
            <CustomSelectField
              groupedOptions={groupedPortfolioOptions}
              id="portfolio-selector"
              label={t('activation_plan.portfolio')}
              options={[]}
              sx={{ minWidth: FIELD_WIDTH }}
              value={portfolioCode}
              variant="outlinedWhite"
              onChange={(e: SelectChangeEvent) => {
                setPortfolioCode(e.target.value)
              }}
            />

            <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
              <CustomFormControl variant="outlinedWhite">
                <DatePicker
                  enableAccessibleFieldDOMStructure={false}
                  format="DD"
                  label={t('activation_plan.delivery_day')}
                  sx={{ width: 180 }}
                  value={deliveryDay}
                  onChange={(newValue) => {
                    if (newValue) {
                      setDeliveryDay(newValue)
                    }
                  }}
                />
              </CustomFormControl>
            </LocalizationProvider>
          </Box>
        }
        pageTitle={t('component.navbar.activation_plan')}
      />

      <Box sx={{ p: 3 }}>
        {!portfolioCode && <Typography>{t('activation_plan.select_portfolio')}</Typography>}
        {portfolioCode && deliveryDayString && (
          <ActivationPlanContent
            key={`${portfolioCode}-${deliveryDayString}`}
            deliveryDay={deliveryDayString}
            portfolioCode={portfolioCode}
          />
        )}
      </Box>
    </>
  )
}

type ActivationPlanContentProps = Readonly<{
  portfolioCode: string
  deliveryDay: string
}>

function ActivationPlanContent({ portfolioCode, deliveryDay }: ActivationPlanContentProps) {
  const { t } = useTranslation()
  const [selectedRegulationObject, setSelectedRegulationObject] = useState<string>('')

  const { data, isLoading, isError } = useActivationSchedulesByRegulationObjectQuery({
    portfolioCode,
    deliveryDay,
  })

  if (isLoading) {
    return <Typography>{t('activation_plan.loading')}</Typography>
  }

  if (isError || !data) {
    return <Typography color="error">{t('activation_plan.error_loading_data')}</Typography>
  }

  const regulationObjects = data.schedules

  if (regulationObjects.length === 0) {
    return <Typography>{t('activation_plan.no_data')}</Typography>
  }

  const selectedSchedule = regulationObjects.find((s) => s.regulationObjectCode === selectedRegulationObject)

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <CustomSelectField
          id="regulation-object-selector"
          label={t('activation_plan.regulation_object')}
          options={[
            { value: '', id: 'regulation-object-empty', label: t('activation_plan.select_regulation_object') },
            ...regulationObjects.map((s) => ({
              value: s.regulationObjectCode ?? '',
              id: `regulation-object-selector-item-${s.regulationObjectCode}`,
              label: s.regulationObjectCode ?? '',
            })),
          ]}
          sx={{ minWidth: FIELD_WIDTH }}
          value={selectedRegulationObject}
          onChange={(e: SelectChangeEvent) => setSelectedRegulationObject(e.target.value)}
        />
        {selectedSchedule && <ActivationPlanChart schedule={selectedSchedule} />}
      </Box>
    </Paper>
  )
}

export default ActivationPlanPage
